/**
 * Quick entry on the sale screen, as in the prototype (handleCode, isDrink, oneTap, tapVi):
 * codes like "2DP", "PEPM" or "CC" at F3, a tile per size for drinks, and the size a product starts
 * with. Pure functions, so the screen only renders.
 */
import type { LineRequest, Menu, Product } from "../../api/types";
import { groupsOf } from "./product-options";

const MAX_QUANTITY = 999;
/** The label of a product with one price (Menu & prijzen): it has no size to type. */
const SINGLE_PRICE = "Standaard";

/** A drink (prototype isDrink): 21% on site, or alcohol. A tile per size, the smallest size by default. */
export function isDrink(menu: Menu, product: Product): boolean {
  if (product.alcohol === true) return true;
  return menu.categories.find((category) => category.id === product.categoryId)?.vat?.onSite === 21;
}

/** Options to choose (choice groups) or ingredients to leave out ("Zonder"): then the dialog opens. */
export function hasOptions(menu: Menu, product: Product): boolean {
  return groupsOf(menu, product).length > 0 || (product.ingredients ?? []).length > 0;
}

/**
 * The size a product starts with: always the smallest, its cheapest size, also for a Klein | Groot (Furkan,
 * 02/11/2026: "hep en kucugu secili olsun"; before, food started on Medium or on the second size). Sizes at the same
 * price: the first of the menu.
 */
export function defaultVariant(product: Product): string | undefined {
  const { variants } = product;
  if (variants.length <= 1) return variants[0]?.label;
  const cheapest = Math.min(...variants.map((variant) => variant.price));
  return variants.find((variant) => variant.price === cheapest)?.label;
}

/** One tap puts it on the ticket (prototype oneTap): nothing to choose, and one size or a drink. */
export function oneTap(menu: Menu, product: Product): boolean {
  return !hasOptions(menu, product) && (product.variants.length <= 1 || isDrink(menu, product));
}

/**
 * The line for a product as the ticket keeps it. A product with one size never carries it, so the same
 * product always adds up on one line, whether it came from a tile, a code or the dialog.
 */
export function lineFor(
  product: Product,
  line: Omit<LineRequest, "productId" | "variant"> & { variant?: string | undefined },
): LineRequest {
  const { variant, ...rest } = line;
  return {
    productId: product.id,
    ...rest,
    ...(variant !== undefined && product.variants.length > 1 && { variant }),
  };
}

/**
 * The size shown after the name of a line (as the server names it): "Coca-Cola 33 cl", "Pepperoni
 * Medium", nothing for a single "Standaard" price. Used until the line is priced.
 */
export function shownVariant(product: Product | undefined, line: Pick<LineRequest, "variant">): string | null {
  if (line.variant !== undefined) return line.variant;
  const only = product?.variants.length === 1 ? product.variants[0]!.label : null;
  return only === SINGLE_PRICE ? null : only;
}

/** The same line for the ticket: a product with one size without it (see lineFor). */
export function normalizedLine(menu: Menu, line: LineRequest): LineRequest {
  const product = menu.products[line.productId];
  return product ? lineFor(product, line) : line;
}

export type CodeResult =
  { kind: "product"; product: Product; variant: string | undefined; quantity: number } | { kind: "unknown" };

/**
 * A code typed at F3 (prototype handleCode): an optional quantity ("2DP", "2xDP", "2*DP"), the code of
 * the product and optionally the first letter of a size ("PEPM" = Pepperoni Medium, "CC" = Coca-Cola).
 * Spaces and case do not matter. A code that starts with digits stays a code ("4F" = Quattro Formaggi,
 * "24F" = 2× Quattro Formaggi). Null for an empty entry.
 */
export function findByCode(menu: Menu, raw: string): CodeResult | null {
  const text = raw.trim().toUpperCase().replace(/\s+/g, "");
  if (text === "") return null;
  const sellable = Object.values(menu.products).filter(
    (product) => product.categoryId !== null && product.code !== undefined,
  );
  const byCode = (code: string) => (code === "" ? undefined : sellable.find((product) => product.code === code));
  /** A code, or a code followed by the first letter of a size; not for a single price. */
  const resolve = (code: string): { product: Product; variant: string | undefined } | null => {
    const product = byCode(code);
    if (product) return { product, variant: undefined };
    const base = byCode(code.slice(0, -1));
    const letter = code.slice(-1);
    const variant = base?.variants.find(
      (item) => item.label !== SINGLE_PRICE && item.label[0]?.toUpperCase() === letter,
    );
    return base && variant ? { product: base, variant: variant.label } : null;
  };

  const whole = resolve(text);
  if (whole) return { kind: "product", ...whole, quantity: 1 };
  // a quantity in front: the longest number that leaves a code ("12DP"), with an optional x or *
  const digits = /^\d+/.exec(text)?.[0] ?? "";
  for (let cut = digits.length; cut >= 1; cut--) {
    const rest = text.slice(cut);
    const found = resolve(rest) ?? (/^[X*]/.test(rest) ? resolve(rest.slice(1)) : null);
    if (found) {
      const quantity = Math.min(MAX_QUANTITY, Math.max(1, Number(digits.slice(0, cut))));
      return { kind: "product", ...found, quantity };
    }
  }
  return { kind: "unknown" };
}

/** "Code PEPM" on a tile with a size, "Code CC" on a drink (prototype: the code as tooltip of a tile). */
export function tileCode(menu: Menu, product: Product, variant: string | undefined): string | undefined {
  if (product.code === undefined) return undefined;
  const letter = variant !== undefined && !isDrink(menu, product) ? (variant[0]?.toUpperCase() ?? "") : "";
  return `${product.code}${letter}`;
}
